import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Leaf, Trash2, Sun, Droplets, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { getSavedPlants, deleteSavedPlant, getFile } from "@/lib/api";

export default function SavedPlantsPage({ deviceId }) {
  const navigate = useNavigate();
  const [plants, setPlants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [imageUrls, setImageUrls] = useState({});

  useEffect(() => {
    loadPlants();
  }, [deviceId]);

  const loadPlants = async () => {
    setLoading(true);
    try {
      const data = await getSavedPlants(deviceId);
      setPlants(data);
      // Load images
      for (const p of data) {
        if (p.image_path) {
          try {
            const url = await getFile(p.image_path);
            setImageUrls((prev) => ({ ...prev, [p.id]: url }));
          } catch {}
        }
      }
    } catch (err) {
      toast.error("Could not load saved plants.");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (plantId) => {
    try {
      await deleteSavedPlant(plantId);
      setPlants((prev) => prev.filter((p) => p.id !== plantId));
      toast.success("Plant removed from collection");
    } catch (err) {
      toast.error("Could not remove plant.");
    }
  };

  return (
    <div className="px-5 pt-6 pb-4" data-testid="saved-plants-page">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => navigate(-1)} data-testid="plants-back-btn" className="w-12 h-12 rounded-xl border-2 border-[#0A2918] flex items-center justify-center press-active hard-shadow-sm bg-white">
          <ArrowLeft size={24} strokeWidth={2.5} />
        </button>
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#0A2918]" style={{ fontFamily: 'Work Sans' }}>
            My Plants
          </h1>
          <p className="text-base text-[#2A4D3B]">{plants.length} saved</p>
        </div>
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="animate-spin text-[#28593A]" size={36} />
        </div>
      )}

      {/* Empty State */}
      {!loading && plants.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 text-center" data-testid="empty-plants">
          <Leaf size={56} strokeWidth={2} className="text-[#28593A]/40 mb-4" />
          <h3 className="text-xl font-bold text-[#0A2918] mb-2" style={{ fontFamily: 'Work Sans' }}>
            No saved plants yet
          </h3>
          <p className="text-base text-[#2A4D3B] mb-6 max-w-xs">
            Identify a plant and save it to start building your collection
          </p>
          <Button
            onClick={() => navigate("/identify")}
            data-testid="goto-identify-btn"
            className="h-14 px-8 text-lg font-bold bg-[#0A2918] text-white rounded-xl press-active hard-shadow"
          >
            Identify a Plant
          </Button>
        </div>
      )}

      {/* Plant Cards */}
      <div className="space-y-4">
        {plants.map((plant, i) => (
          <div
            key={plant.id}
            className="bg-white border-2 border-[#0A2918] rounded-2xl overflow-hidden hard-shadow animate-slide-up"
            style={{ animationDelay: `${i * 80}ms` }}
            data-testid={`plant-card-${i}`}
          >
            <div className="flex">
              {/* Image */}
              {imageUrls[plant.id] ? (
                <img
                  src={imageUrls[plant.id]}
                  alt={plant.name}
                  className="w-28 h-28 object-cover flex-shrink-0"
                />
              ) : (
                <div className="w-28 h-28 bg-[#F2F7F4] flex items-center justify-center flex-shrink-0">
                  <Leaf size={32} strokeWidth={2} className="text-[#28593A]/40" />
                </div>
              )}
              {/* Info */}
              <div className="flex-1 p-4 flex flex-col justify-between">
                <div>
                  <h3 className="text-lg font-bold text-[#0A2918] leading-tight" style={{ fontFamily: 'Work Sans' }}>
                    {plant.name}
                  </h3>
                  {plant.scientific_name && (
                    <p className="text-sm italic text-[#2A4D3B]">{plant.scientific_name}</p>
                  )}
                </div>
                <div className="flex items-center gap-3 mt-2">
                  {plant.sunlight && (
                    <div className="flex items-center gap-1 text-xs font-bold text-[#2A4D3B]">
                      <Sun size={14} strokeWidth={2.5} className="text-[#E25F45]" /> {plant.sunlight}
                    </div>
                  )}
                  {plant.water && (
                    <div className="flex items-center gap-1 text-xs font-bold text-[#2A4D3B]">
                      <Droplets size={14} strokeWidth={2.5} className="text-blue-600" /> {plant.water}
                    </div>
                  )}
                </div>
              </div>
              {/* Delete */}
              <button
                onClick={() => handleDelete(plant.id)}
                data-testid={`delete-plant-${i}`}
                className="w-12 flex items-center justify-center text-[#E25F45] hover:bg-[#E25F45]/10 transition-colors"
              >
                <Trash2 size={20} strokeWidth={2.5} />
              </button>
            </div>
            {plant.care_summary && (
              <div className="px-4 pb-3 pt-1 border-t border-[#0A2918]/10">
                <p className="text-sm text-[#2A4D3B] leading-relaxed">{plant.care_summary}</p>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
